import type { ArchiveEvent } from "@/features/archive/mediators/archiveEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type ArchivePageState = {
  readonly isDesktop: boolean;
  readonly isSettingsOpen: boolean;
  readonly isStorageDisclosureExpanded: boolean;
};

export type ArchivePageEvent = Extract<
  ArchiveEvent,
  {
    type:
      | "archive/search-committed"
      | "archive/search-cleared"
      | "archive/search-scope-changed"
      | "archive/page-change-requested"
      | "archive/page-redirect-requested"
      | "archive/refresh-requested"
      | "archive/retry-requested"
      | "archive/sidebar-toggle-requested"
      | "archive/scroll-requested"
      | "archive/settings-link-clicked"
      | "archive/settings-close-requested"
      | "archive/storage-disclosure-toggle-requested"
      | "archive/viewport-synchronized";
  }
>;

export type ArchivePageEffect =
  | { readonly type: "navigate-page"; readonly page: number }
  | { readonly type: "replace-page"; readonly page: number }
  | { readonly type: "navigate-search"; readonly query?: string }
  | {
      readonly type: "navigate-search-scope";
      readonly scope: Extract<
        ArchivePageEvent,
        { type: "archive/search-scope-changed" }
      >["scope"];
    }
  | { readonly type: "refresh"; readonly manual: boolean }
  | { readonly type: "toggle-sidebar" }
  | { readonly type: "scroll"; readonly edge: "top" | "bottom" };

export function isArchivePageEvent(
  event: ArchiveEvent,
): event is ArchivePageEvent {
  return (
    event.type === "archive/search-committed" ||
    event.type === "archive/search-cleared" ||
    event.type === "archive/search-scope-changed" ||
    event.type === "archive/page-change-requested" ||
    event.type === "archive/page-redirect-requested" ||
    event.type === "archive/refresh-requested" ||
    event.type === "archive/retry-requested" ||
    event.type === "archive/sidebar-toggle-requested" ||
    event.type === "archive/scroll-requested" ||
    event.type === "archive/settings-link-clicked" ||
    event.type === "archive/settings-close-requested" ||
    event.type === "archive/storage-disclosure-toggle-requested" ||
    event.type === "archive/viewport-synchronized"
  );
}

export function createArchivePageMediator(
  isDesktop: boolean,
  runEffect: (effect: ArchivePageEffect) => void,
  restoredState?: ArchivePageState,
): Mediator<ArchivePageState, ArchivePageEvent, ArchivePageEffect> {
  return createMediator<ArchivePageState, ArchivePageEvent, ArchivePageEffect>(
    {
      isDesktop,
      isSettingsOpen: restoredState?.isSettingsOpen ?? false,
      isStorageDisclosureExpanded:
        restoredState?.isStorageDisclosureExpanded ?? false,
    },
    (state, event) => {
      switch (event.type) {
        case "archive/search-committed":
          return {
            status: "handled",
            state,
            effects: [{ type: "navigate-search", query: event.query }],
          };
        case "archive/search-cleared":
          return {
            status: "handled",
            state,
            effects: [{ type: "navigate-search" }],
          };
        case "archive/search-scope-changed":
          return {
            status: "handled",
            state,
            effects: [{ type: "navigate-search-scope", scope: event.scope }],
          };
        case "archive/page-change-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "navigate-page", page: event.page }],
          };
        case "archive/page-redirect-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "replace-page", page: event.page }],
          };
        case "archive/refresh-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "refresh", manual: true }],
          };
        case "archive/retry-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "refresh", manual: false }],
          };
        case "archive/sidebar-toggle-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "toggle-sidebar" }],
          };
        case "archive/scroll-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "scroll", edge: event.edge }],
          };
        case "archive/settings-link-clicked":
          if (event.hasModifier) return { status: "unhandled" };
          return {
            status: "handled",
            state: { ...state, isSettingsOpen: true },
          };
        case "archive/settings-close-requested":
          return {
            status: "handled",
            state: { ...state, isSettingsOpen: false },
          };
        case "archive/storage-disclosure-toggle-requested":
          return {
            status: "handled",
            state: {
              ...state,
              isStorageDisclosureExpanded: !state.isStorageDisclosureExpanded,
            },
          };
        case "archive/viewport-synchronized":
          return {
            status: "handled",
            state: {
              ...state,
              isDesktop: event.isDesktop,
              isSettingsOpen: event.isDesktop ? state.isSettingsOpen : false,
            },
          };
      }
    },
    { runEffect },
  );
}
